import "requestidlecallback-polyfill";
import { EventEmitter } from "events";

import { useCallback, useEffect, useState } from "react";

declare global {
  interface Window {
    RCE5_RENDER_MANAGER: RenderManager;
  }
}

type RenderCallback = () => void;
type RenderQueueItem = RenderCallback | "placeholder";

class RenderManager extends EventEmitter {
  static readonly THRESHOLD = 3;

  private _queue: RenderQueueItem[] = [];
  private _countDeferred = 0;

  constructor() {
    super();
    this.requestImmediate = this.requestImmediate.bind(this);
    this.requestDeferred = this.requestDeferred.bind(this);
  }

  public get count() {
    return this._queue.length;
  }

  public get countDeferred() {
    return this._countDeferred;
  }

  public get available() {
    return this._queue.length < RenderManager.THRESHOLD;
  }

  /**
   * Returns true if the number of rendering editors is below the threshold; otherwise false
   * @returns boolean
   */
  public requestImmediate(): boolean {
    const available = this.available;
    //
    // Queue up a placeholder item so that we know something's rendering
    //
    this._enqueue("placeholder");
    return available;
  }

  public requestDeferred(callback: RenderCallback): boolean {
    if (this.available) {
      //
      // Queue up a placeholder item so that we know something's rendering
      // But call the callback right away since they don't need to wait
      // on another tick
      //
      this._enqueue("placeholder");
      callback();
      return true;
    }

    //
    // Otherwise, defer the callback
    //
    this._enqueue(callback);
    return false;
  }

  private _enqueue(callback: RenderQueueItem) {
    //
    // Start pumping the queue if it's not already
    //
    this._startRelease();

    this._queue.push(callback);
    if (callback !== "placeholder") this._countDeferred++;

    this._emitChange();
  }

  private _startRelease() {
    if (this._queue.length === 0) return;
    window.requestIdleCallback(
      () => {
        this._release();
        this._startRelease();
      },
      { timeout: 10 },
    );
  }

  private _emitChange() {
    //
    // Wrap this in a timeout so it does not happen from within RenderQueue's render method
    // Other components using the hook are going to update state based on this event
    // which causes https://github.com/facebook/react/issues/18178#issuecomment-595846312
    // if we don't push it to the next tick
    //
    setTimeout(() => this.emit("change"));
  }

  private _release() {
    //
    // Dump any placeholders so we can get to the actual deferred renders
    //
    let callback = this._queue.shift();
    while (callback === "placeholder") {
      callback = this._queue.shift();
    }

    if (callback == null) return;

    //
    // Let the next deferred render know it can proceed
    //
    callback();
    this._countDeferred--;

    this._emitChange();
  }
}

window.RCE5_RENDER_MANAGER = window.RCE5_RENDER_MANAGER ?? new RenderManager();

export const requestImmediate = window.RCE5_RENDER_MANAGER.requestImmediate;
export const requestDeferred = window.RCE5_RENDER_MANAGER.requestDeferred;

export const useAreEditorsRendering = () => {
  const [count, setCount] = useState(window.RCE5_RENDER_MANAGER.countDeferred);

  const onChange = useCallback(
    () => setCount(window.RCE5_RENDER_MANAGER.countDeferred),
    [],
  );

  useEffect(() => {
    window.RCE5_RENDER_MANAGER.addListener("change", onChange);
    return () => {
      window.RCE5_RENDER_MANAGER.removeListener("change", onChange);
    };
  });

  return count > 0;
};
